import {
  put,
  takeLatest,
  select,
  call,
  take,
  cancel,
  fork,
  race,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import md5 from 'md5';
import api from '@/api/teacher';
import userApi from '@/api/user';
import apiStudentList from '@/api/student-lists';
import apiClientState from '@/api/client-state';

import { ANALYTICS_SEND_EVENT, ANALYTICS_SET_USER_PROPERTIES } from '@/action-types/analytics';
import {
  ERROR_PASS_REQUEST,
  ERROR_UPDATE_REQUEST,
} from '@/action-types/error';
import {
  TEACHER_CHANGE_STUDENT_REQUEST,
  TEACHER_GET_INFO_REQUEST,
  TEACHER_GET_INFO_SUCCESS,
  GET_STUDENTS_LIST_SUCCESS,
  STUDENTS_LIST_INVALIDATE,
  OPEN_STUDENT_LIST,
  CLOSE_STUDENT_LIST,
  TEACHER_DELETE_STUDENT,
  TEACHER_UPDATE_STUDENT_NOTES,
  GET_STUDENT_AUTH_LINK_REQUEST,
  GET_STUDENT_AUTH_LINK_SUCCESS,
} from '@/action-types/teacher';
import {
  LOGIN_LOGOUT_REQUEST,
} from '@/action-types/login';

import _get from 'lodash/get';
import { USER_ADD_DATA_SUCCESS } from '@/action-types/user';
import {
  APP_TOGGLE_STUDENT_CARD,
  APP_STUDENT_CARD_IS_DIRTY,
} from '@/action-types/app';
import { LOADING_UPDATE_REQUEST } from '@/action-types/loading';
import { Store } from '@/store/smartclass';
import { Action } from 'redux';
import { CLIENT_STATE_UPDATE } from '@/action-types/client-state';

export function* getTeacherInfo(params: { uid: number; }) {
  try {
    // @ts-ignore
    const response = yield call(api.getTeacherInfoForTeacher, params.uid);
    const responseData = response.data.getTeacherInfo[0] || {};
    const responseDataUser = responseData.user;

    // @ts-ignore
    if (window.setSentryUserData) {
      // @ts-ignore
      window.setSentryUserData({
        email: responseDataUser.username,
        role: 'teacher',
      });
    }

    delete responseData.user;

    if (responseDataUser) {
      yield put({ type: TEACHER_GET_INFO_SUCCESS, data: responseData });
      yield put({ type: USER_ADD_DATA_SUCCESS, userData: responseDataUser || {} });
      yield put({
        type: ANALYTICS_SET_USER_PROPERTIES,
        data: {
          email: responseDataUser.username,
        },
      });
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getTeacherInfo'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

export function* getTeacherStudentsList() {
  try {
    const { teacher } : Store = yield select();

    // update only after 10 mins
    // if no data
    // if data is invalid
    if (
      teacher.students.didInvalidate
      || teacher.students.lastUpdated === undefined
      || ((new Date()).getTime() - teacher.students.lastUpdated > 5 * 60 * 1000)
    ) {
      // @ts-ignore
      const response = yield call(api.getTeacherStudentsList, parseInt(teacher.data.id, 10));
      const studentList = response?.data?.getTeacherStudentsList;
      const testUserCourse = _get(response, 'data.getClientState[0]');

      yield put({ type: GET_STUDENTS_LIST_SUCCESS, data: studentList });

      yield put({
        type: ANALYTICS_SET_USER_PROPERTIES,
        data: {
          studentsCount: studentList.length || 0,
        },
      });

      if (testUserCourse) {
        yield put({
          type: CLIENT_STATE_UPDATE,
          state: {
            studentCard: {
              testStudent: {
                courseId: testUserCourse,
              },
            },
          },
        });
      }
    } else {
      // no need update
    }
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getTeacherStudentsList'] });
    yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
  }
}

const createES = (teacherId) => eventChannel((emitter) => {
  const es = new EventSource(`/mercure/.well-known/mercure?topic=teacher_actions_${teacherId}`);

  es.onmessage = (event) => {
    let isNeedToUpdate;

    try {
      isNeedToUpdate = JSON.parse(event.data).action === 'update_student_list';
    } catch (e) {
      Error('data from es server error');
    }

    if (isNeedToUpdate) emitter(true);
  };

  return () => {
    es.close();
  };
});

function* startES({ teacherId }) {
  const action = yield call(createES, teacherId);

  try {
    while (true) {
      const esResponse = yield take(action);

      if (esResponse) {
        yield put({ type: STUDENTS_LIST_INVALIDATE });
        yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'teacher-need-to-update-list' });
      }
    }
  } finally {
    action.close();
  }
}

export function* getUpdateFromServer() {
  while (true) {
    const { teacher } : Store = yield select();
    if (teacher.students.lastUpdated === undefined) {
      if (teacher.data && teacher.data.id) {
        yield race([
          call(startES, { teacherId: teacher.data.id }),
          take(LOGIN_LOGOUT_REQUEST),
        ]);
      } else {
        Error('teacher.data.id undefined');
      }
    }

    yield take(LOGIN_LOGOUT_REQUEST);
  }
}

export function* watchInvalidateTask() {
  try {
    while (true) {
      yield take(STUDENTS_LIST_INVALIDATE);

      yield call(getTeacherStudentsList);
    }
  } finally {
    // task was canceled
  }
}

export function* watchInvalidate() {
  const task = yield fork(watchInvalidateTask);

  yield take(CLOSE_STUDENT_LIST);

  yield cancel(task);
}

export function* teacherUpdateStudent(params: {
  listId: number;
  courseId?: number;
  englishLevel?: number;
  schedule?: string;
}) {
  if (params.listId !== 0) {
    try {
      const response = yield call(
        apiStudentList.updateStudent,
        params.listId,
        params.courseId,
        params.englishLevel,
        params.schedule,
      );

      const { status } = response.data.updateStudentList;

      // TODO dont call refresh, better get data from response
      if (status === 200) yield put({ type: STUDENTS_LIST_INVALIDATE });
    } catch (e) {
      yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'teacherUpdateStudent_1'] });
      yield put({ type: ERROR_UPDATE_REQUEST, isActive: true, errorData: e });
    }
  } else if (params.listId === 0 && params.courseId) {
    const { clientState } : Store = yield select();
    // @ts-ignore
    const clientStateId = clientState.studentCard.testStudent.courseId.id;

    try {
      if (clientStateId) {
        yield call(
          apiClientState.updateClientState,
          clientStateId,
          'studentCard.testStudent.courseId',
          params.courseId.toString(),
        );
      } else {
        yield call(
          apiClientState.setClientState,
          'studentCard.testStudent.courseId',
          params.courseId.toString(),
        );
      }

      // после записи на сервер обновим клиентский стейт
      const testStudentCourseId = _get(
        yield call(apiClientState.getClientState, 'studentCard.testStudent.courseId'),
        'data.getClientState[0]',
      );

      if (testStudentCourseId) {
        yield put({
          type: CLIENT_STATE_UPDATE,
          state: {
            studentCard: {
              testStudent: {
                courseId: testStudentCourseId,
              },
            },
          },
        });
      }
    } catch (e) {
      yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'teacherUpdateStudent_2'] });
    } finally {
      //
    }
  }
}

export function* deleteStudent(
  {
    listId,
  } : {
    listId: number;
  },
) {
  try {
    yield call(
      apiStudentList.deleteStudentFromList,
      listId,
    );

    yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'delete-student' });
    yield put({
      type: APP_TOGGLE_STUDENT_CARD,
      isShow: false,
    });

    yield put({
      type: STUDENTS_LIST_INVALIDATE,
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'deleteStudent'] });
  }
}

export function* updateStudentNotes(
  {
    listId,
    comment,
  } : {
    listId: number;
    comment: string;
  },
) {
  // not test student
  if (listId !== 0) {
    try {
      yield put({ type: LOADING_UPDATE_REQUEST, name: 'updateStudentNotes', state: true });

      yield call(
        apiStudentList.setCommentToCard,
        comment,
        listId,
      );
      yield put({ type: ANALYTICS_SEND_EVENT, eventName: 'update-student-notes' });
      yield put({
        type: APP_STUDENT_CARD_IS_DIRTY,
        isDirty: true,
      });
    } catch (e) {
      yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'updateStudentNotes_1'] });
    } finally {
      yield put({ type: LOADING_UPDATE_REQUEST, name: 'updateStudentNotes', state: false });
    }
  } else {
    const { clientState } : Store = yield select();
    // @ts-ignore
    const clientStateId = clientState.studentCard.testStudent.comment.id;

    yield put({ type: LOADING_UPDATE_REQUEST, name: 'updateStudentNotes', state: true });

    try {
      if (comment && clientStateId) {
        yield call(
          apiClientState.updateClientState,
          clientStateId,
          'studentCard.testStudent.comment',
          comment,
        );
      } else if (comment) {
        yield call(
          apiClientState.setClientState,
          'studentCard.testStudent.comment',
          comment,
        );
      } else if (!comment && clientState) {
        yield call(
          apiClientState.deleteClientState,
          clientStateId,
        );
      }
    } catch (e) {
      yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'updateStudentNotes_2'] });
    } finally {
      yield put({ type: LOADING_UPDATE_REQUEST, name: 'updateStudentNotes', state: false });
    }
  }
}

export function* reloadStudentListData(action : { isShow: boolean; }) {
  const { app } : Store = yield select();

  if (!action.isShow && app.studentCard.isDirty) {
    yield put({
      type: STUDENTS_LIST_INVALIDATE,
    });
    yield put({
      type: APP_STUDENT_CARD_IS_DIRTY,
      isDirty: false,
    });
  }
}

export function* getStudentAuthLink(action: { listId: number; }) {
  try {
    const state = yield select();

    let studentId;
    let studentEmail;

    state.teacher.students.data.forEach((el: any) => {
      if (el.id === action.listId) {
        studentId = el.session.student.user.id;
        studentEmail = el.session.student.user.username;
      }
    });

    const hashData = yield call(userApi.getAuthLink, {
      id: studentId,
      hash: md5(studentId),
    });

    const hash = hashData?.data?.getAuthLink?.hash;

    yield put({
      type: GET_STUDENT_AUTH_LINK_SUCCESS,
      authLinks: {
        ...(state.teacher.authLinks || {}),
        [action.listId]: `${window.location.origin}/restore/${studentEmail}/${hash}`,
      },
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getStudentAuthLink'] });
  }
}

export default function* teacherSaga() {
  // @ts-ignore
  yield takeLatest(TEACHER_GET_INFO_REQUEST, getTeacherInfo);
  yield takeLatest(OPEN_STUDENT_LIST, getTeacherStudentsList);
  yield takeLatest(OPEN_STUDENT_LIST, getUpdateFromServer);
  yield takeLatest(OPEN_STUDENT_LIST, watchInvalidate);
  // @ts-ignore
  yield takeLatest(TEACHER_DELETE_STUDENT, deleteStudent);
  // @ts-ignore
  yield takeLatest(TEACHER_UPDATE_STUDENT_NOTES, updateStudentNotes);
  // @ts-ignore
  yield takeLatest(APP_TOGGLE_STUDENT_CARD, reloadStudentListData);
  // eslint-disdable-next-line
  yield takeLatest<Action & {
    listId: number;
  }>(GET_STUDENT_AUTH_LINK_REQUEST, getStudentAuthLink);
  yield takeLatest<Action & {
    listId: number;
    courseid?: number;
    englishLevel?: number;
    schedule?: string;
  }>(TEACHER_CHANGE_STUDENT_REQUEST, teacherUpdateStudent);
}
